import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Login = () => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const [errors, setErrors] = useState({});
  const [supabaseError, setSupabaseError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo ?? '/profile';

  const translateError = (msg) => {
    if (!msg) return msg;
    if (msg.includes('Password should be at least 6 characters')) return 'La contraseña debe tener al menos 6 caracteres.';
    if (msg.includes('Invalid login credentials')) return 'Correo o contraseña incorrectos.';
    if (msg.includes('User already registered') || msg.includes('already been registered')) return 'Ya existe una cuenta con ese correo.';
    if (msg.includes('Email not confirmed')) return 'Debés confirmar tu correo antes de iniciar sesión.';
    if (msg.includes('Invalid email')) return 'El correo ingresado no es válido.';
    if (msg.includes('Email rate limit exceeded')) return 'Demasiados intentos. Esperá unos minutos e intentá de nuevo.';
    return msg;
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setErrors({});
    setSupabaseError('');
    setSuccessMessage('');
  };

  const handleRegister = async () => {
    setErrors({});
    setSupabaseError('');
    setSuccessMessage('');

    const newErrors = {};
    if (!name) newErrors.name = true;
    if (!email) newErrors.email = true;
    if (!password) newErrors.password = true;
    const phoneRegex = /^\+?[0-9\s\-()]{7,15}$/;
    if (phone && !phoneRegex.test(phone)) newErrors.phone = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({ email, password });
    if (signUpError) {
      setSupabaseError(translateError(signUpError.message));
      setLoading(false);
      return;
    }

    const userId = data.user?.id;
    if (userId) {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{ id: userId, full_name: name, phone, type: 'client', email }]);

      if (profileError) {
        setSupabaseError(translateError(profileError.message));
      } else {
        navigate(returnTo, { state: { message: '¡Bienvenido! Has creado tu cuenta.' } });
      }
    }
    setLoading(false);
  };

  const handleLogin = async () => {
    setErrors({});
    setSupabaseError('');
    setSuccessMessage('');

    const newErrors = {};
    if (!email) newErrors.email = true;
    if (!password) newErrors.password = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setSupabaseError(translateError(error.message));
      setLoading(false);
      return;
    }

    localStorage.setItem('welcomeBack', '¡Bienvenido de nuevo!');
    navigate(returnTo);
  };

  const handleForgotPassword = async () => {
    setErrors({});
    setSupabaseError('');
    setSuccessMessage('');

    if (!email) {
      setErrors({ email: true });
      return;
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`,
    });

    if (error) {
      setSupabaseError(translateError(error.message));
    } else {
      setSuccessMessage('Revisá tu correo para cambiar la contraseña.');
    }
  };

  return (
    <Layout page="login">
      <section className="relative min-h-[80vh] flex items-center justify-center p-4 bg-cream pt-20">
        <div className="bg-card text-ink shadow-lg border border-sand rounded-[4px_60px_4px_60px] p-8 w-full max-w-md">

          {/* Tabs */}
          <div className="flex border-b border-sand mb-6">
            <button
              type="button"
              onClick={() => switchMode('login')}
              className={`flex-1 pb-3 text-sm font-semibold transition border-b-2 ${
                mode === 'login'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-ink/40 hover:text-ink/70'
              }`}
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={`flex-1 pb-3 text-sm font-semibold transition border-b-2 ${
                mode === 'register'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-ink/40 hover:text-ink/70'
              }`}
            >
              Crear cuenta
            </button>
          </div>

          <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>

            {/* NOMBRE (solo registro) */}
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="name">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  placeholder="Tu nombre y apellido"
                  className={`w-full border rounded px-3 py-2 bg-cream ${errors.name ? 'border-red-500' : 'border-sand'}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">Ingresá tu nombre.</p>}
              </div>
            )}

            {/* EMAIL */}
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="email">
                Correo electrónico <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                placeholder="correo@ejemplo.com"
                className={`w-full border rounded px-3 py-2 bg-cream ${errors.email ? 'border-red-500' : 'border-sand'}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">Ingresá un correo válido.</p>}
            </div>

            {/* CONTRASEÑA */}
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="password">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                placeholder="Mínimo 6 caracteres"
                className={`w-full border rounded px-3 py-2 bg-cream ${errors.password ? 'border-red-500' : 'border-sand'}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {errors.password && <p className="text-red-500 text-sm mt-1">Ingresá una contraseña.</p>}
              {mode === 'login' && (
                <button
                  type="button"
                  className="text-sm text-terracotta hover:underline mt-1"
                  onClick={handleForgotPassword}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>

            {/* TELÉFONO (solo registro) */}
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="phone">
                  Teléfono <span className="text-gray-400 text-xs">(opcional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  placeholder="+34 600 000 000"
                  className={`w-full border rounded px-3 py-2 bg-cream ${errors.phone ? 'border-red-500' : 'border-sand'}`}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">Formato de teléfono inválido.</p>}
              </div>
            )}

            {/* ERRORES / ÉXITO */}
            {supabaseError && (
              <p className="text-red-600 text-sm text-center bg-red-50 rounded p-2">{supabaseError}</p>
            )}
            {successMessage && (
              <p className="text-terracotta-dark text-sm text-center bg-sand rounded p-2">{successMessage}</p>
            )}

            {/* BOTÓN PRINCIPAL */}
            <button
              type="button"
              onClick={mode === 'login' ? handleLogin : handleRegister}
              disabled={loading}
              className="w-full py-2 rounded-full bg-terracotta text-cream font-semibold hover:bg-terracotta-dark transition disabled:opacity-60"
            >
              {loading
                ? 'Cargando…'
                : mode === 'login'
                ? 'Iniciar sesión'
                : 'Crear cuenta'}
            </button>

            {/* LINK CAMBIO DE MODO */}
            <p className="text-center text-sm text-ink/60">
              {mode === 'login' ? (
                <>
                  ¿No tenés cuenta?{' '}
                  <button type="button" onClick={() => switchMode('register')} className="text-terracotta hover:underline font-medium">
                    Registrate
                  </button>
                </>
              ) : (
                <>
                  ¿Ya tenés cuenta?{' '}
                  <button type="button" onClick={() => switchMode('login')} className="text-terracotta hover:underline font-medium">
                    Iniciá sesión
                  </button>
                </>
              )}
            </p>

          </form>
        </div>
      </section>
    </Layout>
  );
};

export default Login;
